import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { EventWithMarkets } from '@/feature/event-details-sbk/types';
import { getTeamOrder } from '@/utils/sport-utils';

import { DefaultMatchupCard } from './DefaultMatchupCard';
import { StaticMatchupCard } from './StaticMatchupCard';
import { SoccerMatchupCard } from './soccer/SoccerMatchupCard';
import { TennisMatchupCard } from './tennis/TennisMatchupCard';
import {
    findWinnerMarketCategory,
    getMarketColumnTypes,
    getSelectionCount,
    hasThreeWayResult,
    spreadMarketTypes,
    totalsMarketTypes,
} from './utils';

export type { MarketColumn } from './utils';

type TraditionalMatchupCardProps = {
    event: EventWithMarkets;
    variant?: 'static' | 'dynamic';
    showHeader?: boolean;
    isLastItem?: boolean;
    onPress?: () => void;
};

// Public entry point for sportsbook matchup cards. Derives the market columns once, then
// dispatches to the appropriate variant based on `variant` and sport.
export const TraditionalMatchupCard = ({
    event,
    variant = 'dynamic',
    showHeader,
    isLastItem,
    onPress,
}: TraditionalMatchupCardProps) => {
    const { t } = useTranslation('sbk_market_header');

    const marketColumns = useMemo(() => {
        const markets = event.popular_markets;
        const columnTypes = getMarketColumnTypes(event.sport.name);

        const columnDefinitions = {
            spread: () => {
                const spreadMarkets = markets.find(cat => spreadMarketTypes.includes(cat.market_type.code));
                return {
                    header: t('spread'),
                    marketType: spreadMarkets?.market_type,
                    markets: spreadMarkets?.markets ?? [],
                };
            },
            total: () => {
                const totalsMarkets = markets.find(cat => totalsMarketTypes.includes(cat.market_type.code));
                return {
                    header: t('total'),
                    marketType: totalsMarkets?.market_type,
                    markets: totalsMarkets?.markets ?? [],
                };
            },
            winner: () => {
                const resultMarkets = findWinnerMarketCategory(markets, event.sport.name);
                return {
                    header: t('winner'),
                    marketType: resultMarkets?.market_type,
                    markets: resultMarkets?.markets ?? [],
                };
            },
        };

        return columnTypes.map(type => columnDefinitions[type]());
    }, [event, t]);

    const totalRows = useMemo(() => getSelectionCount(marketColumns), [marketColumns]);
    const isThreeWay = useMemo(() => hasThreeWayResult(marketColumns), [marketColumns]);
    const teamOrder = getTeamOrder(event.sport.name);

    if (variant === 'static') {
        return (
            <StaticMatchupCard
                event={event}
                marketColumns={marketColumns}
                totalRows={totalRows}
                teamOrder={teamOrder}
                showTieRow={isThreeWay}
                onPress={onPress}
            />
        );
    }

    if (event.sport.name === 'Tennis') {
        return (
            <TennisMatchupCard
                event={event}
                marketColumns={marketColumns}
                totalRows={totalRows}
                teamOrder={teamOrder}
                showHeader={showHeader}
                isLastItem={isLastItem}
                onPress={onPress}
            />
        );
    }

    if (isThreeWay) {
        return (
            <SoccerMatchupCard
                event={event}
                marketColumns={marketColumns}
                totalRows={totalRows}
                teamOrder={teamOrder}
                showHeader={showHeader}
                isLastItem={isLastItem}
                onPress={onPress}
            />
        );
    }

    return (
        <DefaultMatchupCard
            event={event}
            marketColumns={marketColumns}
            totalRows={totalRows}
            teamOrder={teamOrder}
            showHeader={showHeader}
            isLastItem={isLastItem}
            onPress={onPress}
        />
    );
};
